import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform, animate } from 'framer-motion';

const Counter = ({ value, duration = 2 }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        // Extract number and suffix (like K, %, etc.)
        const match = value.match(/(\d+)(.*)/);
        if (!match) return;

        const target = parseInt(match[1]);
        const suffix = match[2];

        const controls = animate(0, target, {
            duration: duration,
            ease: "easeOut",
            onUpdate: (latest) => setDisplayValue(Math.floor(latest) + suffix)
        });

        return () => controls.stop();
    }, [value, duration]);

    return <span>{displayValue}</span>;
};

const Stats = () => {
    const stats = [
        { val: '5K+', label: 'Maths Students', suffix: 'Annual' },
        { val: '100+', label: 'Island Rankings', suffix: 'Precision' },
        { val: '10K+', label: 'Problem Sets Solved', suffix: 'Rigorous' },
        { val: '15Y', label: 'Academic Legacy', suffix: 'Established' }
    ];

    return (
        <section className="py-32 bg-[#050505] border-y border-white/[0.03] overflow-hidden relative font-inter">
            {/* Background HUD Graphics */}
            <div className="absolute inset-0 opacity-[0.02] pointer-events-none select-none overflow-hidden">
                <motion.div
                    animate={{
                        x: ['-10%', '10%'],
                        opacity: [0.01, 0.03, 0.01]
                    }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[15rem] font-black text-white leading-none tracking-tighter"
                >
                    CALCULUS
                </motion.div>
            </div>

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-20">
                    {stats.map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1, duration: 0.8 }}
                            viewport={{ once: true }}
                            className="flex flex-col group"
                        >
                            {/* Technical Line Accent */}
                            <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: 48 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5 + i * 0.1, duration: 0.8 }}
                                className="h-[1px] bg-blue-500/30 mb-6 group-hover:w-20 group-hover:bg-blue-500 transition-all duration-500"
                            ></motion.div>

                            <div className="flex items-baseline gap-2 mb-2">
                                <span className="text-4xl md:text-6xl font-black text-white tracking-tighter group-hover:text-blue-500 transition-colors duration-500">
                                    <Counter value={s.val} />
                                </span>
                                <span className="text-[8px] font-black text-blue-500/50 uppercase tracking-widest hidden md:block">
                                    {s.suffix}
                                </span>
                            </div>

                            <div className="space-y-1">
                                <h4 className="text-[10px] font-black text-white uppercase tracking-[0.3em]">
                                    {s.label}
                                </h4>
                                <div className="flex gap-1">
                                    {[...Array(3)].map((_, i) => (
                                        <motion.div
                                            key={i}
                                            animate={{ opacity: [0.1, 0.3, 0.1] }}
                                            transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }}
                                            className="w-1 h-1 rounded-full bg-blue-500"
                                        ></motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Scanning Line HUD */}
            <motion.div
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-500/30 to-transparent"
            />

            <motion.div
                animate={{ x: ['100%', '-100%'] }}
                transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-500/10 to-transparent"
            />
        </section>
    );
};

export default Stats;
